import axiosClient from './axiosClient'
import type {
  TenantConfig,
  LoginRequest,
  LoginResponse,
  GameInfo,
  GameListParams,
  GameRecord,
  GameRoster,
  BattingStats,
  PitchingStats,
  ApiResponse,
  InningScore,
  ScoreStat,
  PositionChange,
  WinExpectancy,
  Wpa,
  PitcherWpa,
  PitchItem,
  PitcherGameResult,
  PitcherPitchDetail,
  BatterGameResult,
  TeamInfo,
  StadiumInfo,
  PlayerMemo,
  PlayerReport,
  Member,
  TrainingVideo,
  AgreePlayer,
  EstGroup,
  EstStandard,
  EstGradeValue,
  EstResult,
  PitcherEstResult,
  BatterEstResult,
  Agree,
  StatSalary,
  AdditionScore,
} from '@/types'

// ── 설정 ─────────────────────────────────────────────────────
export const configApi = {
  getTenantConfig: () =>
    axiosClient.get<{ result: string; resultSet: TenantConfig }>('/config').then((r) => r.data.resultSet),
}

// ── 인증 ─────────────────────────────────────────────────────
export const authApi = {
  login: (data: LoginRequest) =>
    axiosClient.post<LoginResponse>('/auth/login', data).then((r) => r.data),
  logout: () =>
    axiosClient.post('/auth/logout').then((r) => r.data),
  me: () =>
    axiosClient.get<LoginResponse['user']>('/auth/me').then((r) => r.data),
}

// ── 경기 ─────────────────────────────────────────────────────
export const gamesApi = {
  list: ({ yearMonth, gameTypeCode, teamId }: GameListParams) => {
    const [season, month] = yearMonth.split('/')
    return axiosClient.get<ApiResponse<{ resultList: GameInfo[] }>>('/v1/games', {
      params: {
        season,
        month,
        ...(gameTypeCode && { gameTypeCode }),
        ...(teamId && { teamId }),
      },
    }).then((r) => r.data)
  },
  get: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ gameInfo: GameInfo }>>(`/games/${gameInfoId}`).then((r) => r.data),
  create: (data: Partial<GameInfo>) =>
    axiosClient.post<ApiResponse<{ gameInfoId: string }>>('/games', data).then((r) => r.data),
  update: (gameInfoId: string, data: Partial<GameInfo>) =>
    axiosClient.put<ApiResponse<void>>(`/games/${gameInfoId}`, data).then((r) => r.data),
  delete: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}`).then((r) => r.data),
  patchComplete: (gameInfoId: string, completeYn: string) =>
    axiosClient.patch<ApiResponse<void>>(`/games/${gameInfoId}/complete`, { completeYn }).then((r) => r.data),
  deleteRoster: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}/roster`).then((r) => r.data),
  deleteRecords: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}/records`).then((r) => r.data),
  // 라인업
  getRoster: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ rosterList: GameRoster[] }>>(`/games/${gameInfoId}/roster`).then((r) => r.data),
  saveAdditionScore: (data: {
    gameInfoId: string | number
    playerId: number
    additionScoreType: string
    additionScore: number
    additionComment?: string
  }) =>
    axiosClient.post<ApiResponse<void>>('/v1/additionScore', data).then((r) => r.data),
  getAdditionScores: (gameInfoId: string | number) =>
    axiosClient.get<ApiResponse<{ groupList: AdditionScore[] }>>(
      '/v1/additionScore', { params: { gameInfoId } }
    ).then((r) => r.data),
  deleteAdditionScore: (id: number) =>
    axiosClient.delete<ApiResponse<void>>(`/v1/additionScore/${id}`).then((r) => r.data),
  bulkDeleteAdditionScores: (ids: number[]) =>
    axiosClient.delete<ApiResponse<void>>('/v1/additionScore', { data: { additionScoreIds: ids } }).then((r) => r.data),
  gameResult: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<unknown>>('/v1/games/gameResult', { params: { gameInfoId } }).then((r) => r.data),
}

// ── 기록 ─────────────────────────────────────────────────────
export const recordsApi = {
  listByGame: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ recordList: GameRecord[] }>>(`/games/${gameInfoId}/records`).then((r) => r.data),
  get: (gameInfoId: string, recordId: string) =>
    axiosClient.get<ApiResponse<{ record: GameRecord }>>(`/games/${gameInfoId}/records/${recordId}`).then((r) => r.data),
}

// ── 입력기 전용 (RecorderController) ──────────────────────────
export const recorderApi = {
  init: (gameInfoId: string) =>
    axiosClient.post<ApiResponse<unknown>>('/recorder/init', { gameInfoId }).then((r) => r.data),
  synchronize: (data: unknown) =>
    axiosClient.post<ApiResponse<unknown>>('/recorder/synchronize', data).then((r) => r.data),
}

// ── 통계 ─────────────────────────────────────────────────────
export const statsApi = {
  batting: (params: { year: string; teamId?: string; gameType?: string }) =>
    axiosClient.get<ApiResponse<{ statList: BattingStats[] }>>('/stats/batting', { params }).then((r) => r.data),
  pitching: (params: { year: string; teamId?: string; gameType?: string }) =>
    axiosClient.get<ApiResponse<{ statList: PitchingStats[] }>>('/stats/pitching', { params }).then((r) => r.data),
}

// ── 경기 확장 ─────────────────────────────────────────────────
export const gameViewApi = {
  inningScore: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ scores: InningScore[]; lastInning: Record<string, number>; scoreStat: ScoreStat[] }>>(
      `/games/${gameInfoId}/inning-score`
    ).then((r) => r.data),

  positionChanges: (gameInfoId: string, rosterId: string) =>
    axiosClient.get<ApiResponse<{ positionChangeList: PositionChange[] }>>(
      `/games/${gameInfoId}/position-changes/${rosterId}`
    ).then((r) => r.data),

  winExpectancy: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{
      winExpectancy: WinExpectancy[]
      wpa: { away: Wpa[]; home: Wpa[] }
      pitcherWpa: { away: PitcherWpa[]; home: PitcherWpa[] }
    }>>(`/games/${gameInfoId}/win-expectancy`).then((r) => r.data),

  teams: () =>
    axiosClient.get<ApiResponse<{ teamList: TeamInfo[] }>>('/v1/teams').then((r) => r.data),

  stadiums: () =>
    axiosClient.get<ApiResponse<{ stadiumList: StadiumInfo[] }>>('/v1/stadiums').then((r) => r.data),
}

// ── 기록 뷰 ──────────────────────────────────────────────────
export const recordViewApi = {
  pitches: (gameInfoId: string, params?: { pitcherRosterId?: string; ballCode?: string; inning?: string }) =>
    axiosClient.get<ApiResponse<{ pitchList: PitchItem[] }>>(
      `/games/${gameInfoId}/pitches`, { params }
    ).then((r) => r.data),

  operation: (gameInfoId: string, gameRecordId: string) =>
    axiosClient.get<ApiResponse<{ operationList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/records/${gameRecordId}/operation`
    ).then((r) => r.data),

  memos: (gameInfoId: string, membersId?: string) =>
    axiosClient.get<ApiResponse<{ memoList: PlayerMemo[] }>>(
      `/games/${gameInfoId}/memos`, { params: { membersId } }
    ).then((r) => r.data),

  reports: (gameInfoId: string, params?: { membersId?: string; userId?: string; reportType?: string }) =>
    axiosClient.get<ApiResponse<{ reportList: PlayerReport[] }>>(
      `/games/${gameInfoId}/reports`, { params }
    ).then((r) => r.data),

  notes: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<Record<string, unknown>>>(`/games/${gameInfoId}/notes`).then((r) => r.data),

  textViewInning: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ inningList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/inning`
    ).then((r) => r.data),

  textViewPitcherResult: (gameInfoId: string, params: { year: string; teamId: string; gameTypeCode?: string }) =>
    axiosClient.get<ApiResponse<{ pitcherResultList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/pitcher-result`, { params }
    ).then((r) => r.data),

  textViewBatterResult: (gameInfoId: string, params: { year: string; teamId: string; gameTypeCode?: string }) =>
    axiosClient.get<ApiResponse<{ batterResultList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/batter-result`, { params }
    ).then((r) => r.data),
}

// ── 선수 성적 ─────────────────────────────────────────────────
export const memberStatsApi = {
  pitcherResult: (memberId: string, params?: {
    year?: string; gameTypeCode?: string; month?: string
    ballCount?: string; strikeCount?: string; outCount?: string; runnerState?: string
  }) =>
    axiosClient.get<ApiResponse<{ enterInfo: PitcherGameResult[]; pitchDetail: PitcherPitchDetail[] }>>(
      `/members/${memberId}/pitcher-result`, { params }
    ).then((r) => r.data),

  batterResult: (memberId: string, params?: {
    year?: string; gameTypeCode?: string; month?: string
    ballCount?: string; strikeCount?: string; outCount?: string; runnerState?: string
  }) =>
    axiosClient.get<ApiResponse<{ batterResultList: BatterGameResult[] }>>(
      `/members/${memberId}/batter-result`, { params }
    ).then((r) => r.data),
}

// ── 데이터 관리 (관리자) ──────────────────────────────────────
export const dataApi = {
  games: (params?: { year?: string; month?: string; day?: string }) =>
    axiosClient.get<ApiResponse<{ gameList: Record<string, unknown>[] }>>(
      '/data/games', { params }
    ).then((r) => r.data),

  rosterDetail: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ rosterList: Record<string, unknown>[] }>>(
      `/data/games/${gameInfoId}/roster`
    ).then((r) => r.data),

  recordOverview: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ recordList: Record<string, unknown>[] }>>(
      `/data/games/${gameInfoId}/record`
    ).then((r) => r.data),

  recordDetail: (gameInfoId: string, gameRecordId: string) =>
    axiosClient.get<ApiResponse<{ record: Record<string, unknown> }>>(
      `/data/games/${gameInfoId}/records/${gameRecordId}`
    ).then((r) => r.data),
}

// ── 선수 CRUD ─────────────────────────────────────────────────
export const playersApi = {
  list: (params?: { teamId?: string; keyword?: string }) =>
    axiosClient.get<ApiResponse<{ memberList: Member[] }>>('/members', { params }).then((r) => r.data),
  create: (data: Partial<Member>) =>
    axiosClient.post<ApiResponse<{ memberId: string }>>('/members', data).then((r) => r.data),
  update: (memberId: string, data: Partial<Member>) =>
    axiosClient.put<ApiResponse<void>>(`/members/${memberId}`, data).then((r) => r.data),
}

// ── 훈련영상 CRUD ─────────────────────────────────────────────
export const trainingVideoApi = {
  list: (params?: { teamId?: string; keyword?: string; page?: number; size?: number }) =>
    axiosClient.get<ApiResponse<{ videoList: TrainingVideo[]; total: number }>>('/training-videos', { params }).then((r) => r.data),
  create: (data: Partial<TrainingVideo>) =>
    axiosClient.post<ApiResponse<{ videoId: string }>>('/training-videos', data).then((r) => r.data),
  update: (videoId: string, data: Partial<TrainingVideo>) =>
    axiosClient.put<ApiResponse<void>>(`/training-videos/${videoId}`, data).then((r) => r.data),
  delete: (videoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/training-videos/${videoId}`).then((r) => r.data),
}

// ── 전력분석 ──────────────────────────────────────────────────
export const strategyApi = {
  applyToGame: (gameInfoId: string) =>
    axiosClient.post<ApiResponse<void>>(`/games/${gameInfoId}/strategy/apply`).then((r) => r.data),
}

// ── 평가 관리 ─────────────────────────────────────────────────
export const estimateApi = {
  groups: {
    list: (season?: string) =>
      axiosClient.get<ApiResponse<{ groupList: EstGroup[] }>>('/v1/groups', { params: { season } }).then((r) => r.data),
    create: (data: Partial<EstGroup>) =>
      axiosClient.post<ApiResponse<{ groupId: number }>>('/v1/groups', data).then((r) => r.data),
    update: (groupId: number, data: Partial<EstGroup>) =>
      axiosClient.post<ApiResponse<void>>(`/v1/groups/${groupId}`, data).then((r) => r.data),
    delete: (groupId: number) =>
      axiosClient.delete<ApiResponse<void>>(`/v1/groups/${groupId}`).then((r) => r.data),
  },
  standards: {
    list: (groupId: number, playerType?: string) =>
      axiosClient.get<ApiResponse<{ standardList: EstStandard[] }>>(
        `/v1/groups/${groupId}/standards`, { params: { playerType } }
      ).then((r) => r.data),
    save: (groupId: number, playerType: string, standards: EstStandard[]) =>
      axiosClient.post<ApiResponse<void>>(
        `/v1/groups/${groupId}/standards`, standards, { params: { playerType } }
      ).then((r) => r.data),
  },
  gradeValues: {
    list: (groupId: number) =>
      axiosClient.get<ApiResponse<{ gradeValueList: EstGradeValue[] }>>(
        `/v1/groups/${groupId}/grade-values`
      ).then((r) => r.data),
    save: (groupId: number, gradeValues: EstGradeValue[]) =>
      axiosClient.post<ApiResponse<void>>(
        `/v1/groups/${groupId}/grade-values`, gradeValues
      ).then((r) => r.data),
  },
  results: (params: {
    groupId?: number
    season?: string
    playerType?: string
    teamId?: string
    memberName?: string
  }) =>
    axiosClient.get<ApiResponse<{ resultList: EstResult[] }>>('/v1/results', { params }).then((r) => r.data),
  pitcherResults: (params: {
    groupId?: number
    season?: string
    teamId?: string
    memberName?: string
  }) =>
    axiosClient.get<ApiResponse<{ resultList: PitcherEstResult[] }>>('/v1/results', {
      params: { ...params, playerType: 'pitcher' },
    }).then((r) => r.data),
  batterResults: (params: {
    groupId?: number
    season?: string
    teamId?: string
    memberName?: string
  }) =>
    axiosClient.get<ApiResponse<{ resultList: BatterEstResult[] }>>('/v1/results', {
      params: { ...params, playerType: 'batter' },
    }).then((r) => r.data),
  calculate: (groupId: number, season?: string) =>
    axiosClient.post<ApiResponse<{ calculated: number }>>('/v1/calculate', null, {
      params: { groupId, season },
    }).then((r) => r.data),
}

// ── 선수 조회 (팀별) ──────────────────────────────────────────
export const playerApi = {
  listByTeam: (teamId: string) =>
    axiosClient.get<ApiResponse<{ playerList: AgreePlayer[] }>>('/v1/player', { params: { teamId } }).then((r) => r.data),
}

// ── 연봉협상 게시판 ───────────────────────────────────────────
export const agreesApi = {
  list: (params?: {
    season?: string
    teamId?: string
    playerName?: string
    content?: string
    agencyName?: string
    pContentId?: number
    playerId?: number
    findType?: string
  }) =>
    axiosClient.get<ApiResponse<{ agreesList?: Agree[]; agreeList?: Agree[] }>>('/v1/agrees', { params }).then((r) => r.data),
  listByPlayer: (playerId: number, season?: string) =>
    axiosClient.get<ApiResponse<{ agreesList?: Agree[]; agreeList?: Agree[] }>>(`/v1/agrees/player/${playerId}`, { params: { season } }).then((r) => r.data),
  get: (agreesId: number) =>
    axiosClient.get<ApiResponse<{ agree: Agree }>>(`/v1/agrees/${agreesId}`).then((r) => r.data),
  create: (data: Partial<Agree>) =>
    axiosClient.post<ApiResponse<{ agreesId: number }>>('/v1/agrees', data).then((r) => r.data),
  update: (agreesId: number, data: Partial<Agree>) =>
    axiosClient.post<ApiResponse<void>>(`/v1/agrees/${agreesId}`, data).then((r) => r.data),
  delete: (agreesId: number) =>
    axiosClient.post<ApiResponse<void>>(`/v1/agrees/${agreesId}/delete`).then((r) => r.data),
}

// ── Stat&연봉 비교 ────────────────────────────────────────────
export const statSalaryApi = {
  list: (params?: {
    playerType?: string
    nowFromSalary?: number
    nowToSalary?: number
    nextFromSalary?: number
    nextToSalary?: number
  }) =>
    axiosClient.get<ApiResponse<{ statSalaryList: StatSalary[] }>>('/v1/statSalary', { params }).then((r) => r.data),
  calculate: (season: string) =>
    axiosClient.post<ApiResponse<{ calculated: number }>>('/v1/statSalary/calculate', null, {
      params: { season },
    }).then((r) => r.data),
}
